import { Injectable } from '@angular/core'
import { LoginModel } from './login/login_model'
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http'
import { Observable, throwError } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { ToastrService } from 'ngx-toastr'
import { AuthStateService } from './auth-state.service'
import { environment } from '../environments/environment'
import { UserModel } from './sections/users/user_model'
import { Router } from '@angular/router'

const API_URL = environment.apiUrl

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private userInfo: UserModel | null = null

  constructor(
    private http: HttpClient,
    private authStateService: AuthStateService,
    private toastr: ToastrService,
    private router: Router,
  ) {}

  login(model: LoginModel): Observable<UserModel> {
    return this.http
      .post<UserModel>(API_URL + `/Auth/Login`, model,{
        withCredentials: true,
      })
      .pipe(
        map((response: UserModel) => {
          if (response) {
            this.userInfo = response
          }
          return response
        }),
        catchError((error: any) => {
          this.toastr.error('Login failed')
          this.logout()
          return this.handleError(error)
        }),
      )
  }

  getPermissions(username: string | null): Observable<any> {
    this.userInfo = this.authStateService.getUserInformation()
    if (this.userInfo === null || this.userInfo === undefined) {
      return this.handleError(
        Error('Failed to fetch permissions. Please login and try again.'),
      )
    }
    var user = username
    if(username == null){
      user = this.userInfo.username
    }
    return this.http
      .get<any>(
        API_URL + `/Auth/GetPermissions?username=${user}`,
        {
          withCredentials: true,
        },
      )
      .pipe(
        map((response: any) => {
          return response
        }),
        catchError((error: any) => {
          this.toastr.error(
            'Failed to fetch permissions. Please login and try again.',
          )
          return this.handleError(error)
        }),
      )
  }

  register(model: UserModel): Observable<UserModel> {
    return this.http
      .post<UserModel>(API_URL + `/Auth/CreateUser`, model, {
        withCredentials: true,
      })
      .pipe(
        map((response: UserModel) => {
          return response
        }),
        catchError((error: any) => {
          this.toastr.error('Registration failed')
          return this.handleError(error)
        }),
      )
  }

  updateUser(model: UserModel): Observable<UserModel> {
    return this.http
      .post<UserModel>(API_URL + `/Auth/UpdateUser`, model, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        withCredentials: true,
      })
      .pipe(
        map((response: UserModel) => {
          return response
        }),
        catchError((error: any) => {
          this.toastr.error('Registration failed')
          return this.handleError(error)
        }),
      )
  }

  addRole(role: string, isAdmin: boolean): Observable<string> {
    return this.http
      .post<string>(
        API_URL + `/Auth/CreateRole`,
        { role, isAdmin },
        {
          withCredentials: true,
        },
      )
      .pipe(
        map((response: string) => {
          return response
        }),
        catchError((error: any) => {
          this.toastr.error('Registration failed')
          return this.handleError(error)
        }),
      )
  }

  logout(): void {
    this.userInfo = null
    this.authStateService.logout()
  }

  getUsers(): Observable<UserModel[]> {
    return this.http
      .get<UserModel[]>(API_URL + '/Auth/GetAllUsers', {
        withCredentials: true,
      })
      .pipe(
        map((response: any) => {
          return response
        }),
        catchError((error: any) => {
          if (error.status === 401) {
            this.authStateService.setLoggedIn(false)
            this.logout()
            this.router.navigateByUrl('/auth/login')
            return this.handleError({
              status: 401,
              message: 'Session Expired. Please log in again',
            })
          } else if (error.status === 500) {
            alert('An error occurred. Please try again later.')
          }

          return this.handleError(error)
        }),
      )
  }
  getAllRoles(): Observable<string[]> {
    return this.http
      .get<string[]>(API_URL + '/Auth/GetAllRoles', {
        withCredentials: true,
      })
      .pipe(
        map((response: any) => {
          return response.response
        }),
        catchError((error: any) => {
          return this.handleError(error)
        }),
      )
  }

  getUserInfo(): UserModel | null {
    return this.userInfo
  }

   private handleError(error: any): Observable<never> {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: error.status,
          statusText: error.message,
        }),
    )
  }

  pingServer(): Observable<void> {
    return this.http.get(API_URL +'/Auth/ping', { withCredentials: true }).pipe(
        map(() => {
          return
        }),
        catchError((error: any) => {
          if (error.status === 401) {
            this.logout()
            this.authStateService.setLoggedIn(false)
          }
          return this.handleError(error)
        }),
      );
  }

  loadSession(): Promise<void> {
  return new Promise((resolve) => {
    this.pingServer().subscribe({
      next: () => {
        this.authStateService.setSessionReady(true);
        resolve();
      },
      error: () => {
        this.authStateService.setSessionReady(true);
        resolve();
      },
    });
  });
}
}
